/*
 * Provider und Context für die Datenquelle.
 *
 * Vorher nahm jeder Renderer `source` als Prop, und die einzige Stelle, die
 * sie besetzte, war die Simulator-Preview. Bei 22 Renderern und
 * verschachtelten Compound-Tiles heißt das: jede neue Seite muss die Quelle
 * durch jede Ebene durchreichen, und wer es einmal vergisst, bekommt einen
 * Typfehler an einer Stelle, die mit dem eigentlichen Problem nichts zu tun
 * hat.
 *
 * Ab jetzt gilt: **Prop hat Vorrang, Context ist der Default.** Die
 * Renderer-Signaturen bleiben unverändert (`source` ist dort weiter
 * verpflichtend) — die Auflösung passiert eine Ebene darüber in
 * {@link WidgetHost}. Damit ändert sich für die 22 bestehenden Renderer
 * keine Zeile.
 *
 * Zwei Provider:
 *
 *   - {@link PointBindingSourceProvider} — nimmt eine fertige Quelle. Das
 *     ist der Weg für den Simulator (Widget-Katalog) und für Tests.
 *   - {@link LivePlantBindingProvider} — baut die Live-Quelle für genau eine
 *     Anlage: Hub-Abo per `useLivePlant`, Werte-Snapshot einmalig per
 *     REST, und daraus eine `SignalRPointBindingSource`.
 */

import { createContext } from 'preact';
import type { ComponentChildren } from 'preact';
import { useContext, useEffect, useMemo, useRef } from 'preact/hooks';
import { api } from '../api/client';
import type { GetValuesSnapshotResponse } from '../api/types';
import {
  hydratePlantSnapshot,
  markPlantHydrated,
  releaseSnapshotFetch,
  tryClaimSnapshotFetch,
  useLivePlant,
} from '../realtime/signalStore';
import type { IPointBindingSource } from './binding';
import type { HmiLeafInfo } from './staleness';
import { SignalRPointBindingSource } from './SignalRSource';

const PointBindingSourceContext = createContext<IPointBindingSource | null>(null);

interface ProviderProps {
  source: IPointBindingSource;
  children?: ComponentChildren;
}

/** Stellt eine beliebige Quelle für den Unterbaum bereit. */
export function PointBindingSourceProvider({ source, children }: ProviderProps) {
  return (
    <PointBindingSourceContext.Provider value={source}>
      {children}
    </PointBindingSourceContext.Provider>
  );
}

/**
 * Die Quelle aus dem Context, oder `null` wenn keine gesetzt ist.
 *
 * Bewusst nicht werfend: ein Widget-Katalog-Eintrag oder eine halb migrierte
 * Seite soll eine lesbare Kachel zeigen ("keine Datenquelle") statt den
 * ganzen Router-Teilbaum zu sprengen.
 */
export function useOptionalPointBindingSource(): IPointBindingSource | null {
  return useContext(PointBindingSourceContext);
}

/**
 * Die Quelle aus dem Context. Wirft, wenn keine gesetzt ist — für Aufrufer,
 * die ohne Datenquelle strukturell nichts Sinnvolles tun können.
 */
export function usePointBindingSource(): IPointBindingSource {
  const source = useContext(PointBindingSourceContext);
  if (!source) {
    throw new Error(
      'Keine IPointBindingSource im Context. Unterbaum in <PointBindingSourceProvider> '
      + 'oder <LivePlantBindingProvider> einwickeln.',
    );
  }
  return source;
}

interface LiveProviderProps {
  /** Anlage, deren Live-Werte dieser Unterbaum sieht. */
  plantId: string;
  /**
   * Schema-Angaben je Leaf-Guid, aus dem Anlagen-Schema. Optional.
   *
   * Trägt zwei Dinge: den Anzeigenamen für Command-Toasts und das
   * Poll-/Heartbeat-Intervall, aus dem die Alters-Schwelle des Datenpunkts
   * abgeleitet wird (`hmi/staleness.ts`). Fehlt der Lookup, gilt für beides
   * der dort dokumentierte konservative Fallback.
   */
  leafInfoFor?: (leafId: string) => HmiLeafInfo | undefined;
  children?: ComponentChildren;
}

/**
 * Live-Datenquelle für eine Anlage.
 *
 * Macht drei Dinge, alle über bestehende Infrastruktur:
 *
 *   1. `useLivePlant(plantId)` — Refcount-Abo auf `values-{plantId}`. Erbt
 *      Karenz, Visibility-Gating und Re-Subscribe nach Reconnect vom
 *      Signal-Store.
 *   2. Einmaliger `GET /api/plants/{id}/values` und Merge in die
 *      Leaf-Signale. Ohne diesen Snapshot zeigt jedes Widget so lange
 *      nichts, bis der Datenpunkt sich zufällig ändert — bei Deadband-
 *      Publishing können das Minuten sein. Dieselbe Sequenz wie in
 *      `pages/PlantSchemaPage.tsx`; `tryClaimSnapshotFetch` verhindert,
 *      dass ein gleichzeitig offener DP-Viewer denselben Abruf doppelt
 *      macht.
 *   3. Eine `SignalRPointBindingSource` daraus bauen und in den Context
 *      stellen.
 *
 * Die Quelle akzeptiert nur Binding-Keys DIESER Anlage — ein Key mit einer
 * fremden plantId bekommt einen sichtbaren `bad`-Wert. Mehr-Anlagen-Screens
 * bräuchten hier einen Provider pro Anlage; siehe Bericht.
 */
export function LivePlantBindingProvider({ plantId, leafInfoFor, children }: LiveProviderProps) {
  const live = useLivePlant(plantId);

  // Werte-Snapshot. Ein Effekt pro plantId; die Claim-Logik im Signal-Store
  // entscheidet, ob dieser Mount den Abruf wirklich fährt.
  useEffect(() => {
    if (!tryClaimSnapshotFetch(plantId)) return undefined;
    const controller = new AbortController();
    let cancelled = false;
    void (async () => {
      try {
        const response = await api.get<GetValuesSnapshotResponse>(
          `/api/plants/${plantId}/values`,
          controller.signal,
        );
        if (cancelled) return;
        hydratePlantSnapshot(plantId, response.values);
      } catch (err) {
        if (cancelled || controller.signal.aborted) return;
        console.warn(`[hmi] Werte-Snapshot für Anlage ${plantId} fehlgeschlagen`, err);
        // Hydration trotzdem als "versucht" markieren, sonst dreht sich der
        // Spinner für immer.
        markPlantHydrated(plantId);
      } finally {
        releaseSnapshotFetch(plantId);
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [plantId]);

  // `leafInfoFor` kommt vom Aufrufer und wechselt typischerweise EINMAL —
  // nämlich wenn das Anlagen-Schema geladen ist und die Tabelle steht. Es
  // darf trotzdem nicht in die Deps der Quelle: der Signal-Cache in
  // `pointSignal` ist per Quellen-Instanz geschlüsselt (WeakMap), eine neue
  // Quelle würde also jedes offene Punkt-Abo schließen und neu aufbauen.
  // Deshalb liegt die Funktion in einem Ref, den die Quelle über einen
  // stabilen Wrapper liest.
  const leafInfoRef = useRef(leafInfoFor);
  leafInfoRef.current = leafInfoFor;

  const source = useMemo(
    () => new SignalRPointBindingSource({
      lookupPlant: (requestedPlantId) => (requestedPlantId === plantId ? live : null),
      leafInfoFor: (leafId) => leafInfoRef.current?.(leafId),
    }),
    // `live` ist per `useMemo` im Signal-Store stabil je plantId. Nur diese
    // zwei Deps — siehe die Begründung zum Ref oben.
    [plantId, live],
  );

  return (
    <PointBindingSourceContext.Provider value={source}>
      {children}
    </PointBindingSourceContext.Provider>
  );
}
